import React, { useState, useMemo } from 'react';
import {
  Objective,
  Milestone,
  ReviewType,
  RatingScale,
  ObjectiveEvaluationSummary,
  ObjectiveRating,
  CreateReviewInput,
  Review,
} from '../../types/pms';
import { calculateWeightedRating, getRatingLabel } from '../../services/pmsRatingCalculator';
import ProgressBar from './ProgressBar';
import ProgressSlider from './ProgressSlider';
import RatingDisplay from './RatingDisplay';
import './ReviewForm.css';

interface ReviewFormProps {
  /** Review type: mid-year, end-of-year, or quarterly */
  reviewType: ReviewType;
  /** Employee being reviewed */
  employeeId: string;
  employeeName: string;
  /** Review cycle ID */
  reviewCycleId: string;
  /** Employee objectives for the review cycle */
  objectives: Objective[];
  /** Milestones keyed by objective ID */
  milestonesByObjective: Record<string, Milestone[]>;
  /**
   * The employee's own self-review, if they've submitted one — shown read-only
   * above the manager's inputs. The caller (route wrapper) is responsible for not
   * rendering this form at all until canManagerReview(cycle, review) allows it.
   */
  employeeSelfReview?: Review;
  /** Called when the review is submitted */
  onSubmit: (input: CreateReviewInput) => void;
  /** Called when the form is cancelled */
  onCancel?: () => void;
  /** Whether the form is in a submitting state */
  submitting?: boolean;
}

const RATING_OPTIONS: { value: RatingScale; label: string }[] = [
  { value: RatingScale.OUTSTANDING, label: 'Outstanding (5)' },
  { value: RatingScale.EXCEEDS_EXPECTATIONS, label: 'Exceeds Expectations (4)' },
  { value: RatingScale.MEETS_EXPECTATIONS, label: 'Meets Expectations (3)' },
  { value: RatingScale.NEEDS_IMPROVEMENT, label: 'Needs Improvement (2)' },
  { value: RatingScale.UNSATISFACTORY, label: 'Unsatisfactory (1)' },
];

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1' },
  { value: 2, label: 'Q2' },
  { value: 3, label: 'Q3' },
  { value: 4, label: 'Q4' },
];

const EVALUATION_SUMMARY_OPTIONS = [
  ObjectiveEvaluationSummary.GOAL_ACHIEVED,
  ObjectiveEvaluationSummary.PARTIALLY_ACHIEVED,
  ObjectiveEvaluationSummary.NOT_ACHIEVED,
];

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function getReviewTypeLabel(type: ReviewType): string {
  switch (type) {
    case ReviewType.END_YEAR: return 'End-of-Year Review';
    case ReviewType.MID_YEAR: return 'Mid-Year Review';
    case ReviewType.QUARTERLY: return 'Quarterly Review';
  }
}

function getTypeBadgeClass(type: ReviewType): string {
  switch (type) {
    case ReviewType.END_YEAR: return 'pms-review-form__type-badge--end-year';
    case ReviewType.MID_YEAR: return 'pms-review-form__type-badge--mid-year';
    case ReviewType.QUARTERLY: return 'pms-review-form__type-badge--quarterly';
  }
}

/**
 * ReviewForm Component
 *
 * Supports mid-year, end-of-year, and quarterly review types.
 * - End-of-year: per-objective rating, final progress, comments, overall rating, development suggestions
 * - Mid-year: per-objective interim comments and overall progress assessment
 * - Quarterly: quarterly comments with quarter identifier
 * Calculates and displays suggested overall rating as weighted average, allows manager override.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 10.1, 10.2, 21.1, 21.2
 */
const ReviewForm: React.FC<ReviewFormProps> = ({
  reviewType,
  employeeId,
  employeeName,
  reviewCycleId,
  objectives,
  milestonesByObjective,
  employeeSelfReview,
  onSubmit,
  onCancel,
  submitting = false,
}) => {
  // Per-objective state for end-of-year reviews
  const [objectiveRatings, setObjectiveRatings] = useState<Record<string, RatingScale | ''>>({});
  const [objectiveFinalProgress, setObjectiveFinalProgress] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    objectives.forEach((o) => { init[o.id] = o.progressPercentage; });
    return init;
  });
  const [objectiveComments, setObjectiveComments] = useState<Record<string, string>>({});
  const [objectiveEvaluationSummary, setObjectiveEvaluationSummary] = useState<Record<string, ObjectiveEvaluationSummary | ''>>({});

  // Overall fields
  const [overallRating, setOverallRating] = useState<RatingScale | ''>('');
  const [overallComments, setOverallComments] = useState('');
  const [developmentSuggestions, setDevelopmentSuggestions] = useState('');

  // Quarterly-specific
  const [quarter, setQuarter] = useState<number>(1);

  // Validation errors
  const [errors, setErrors] = useState<string[]>([]);

  // Calculate suggested rating from per-objective ratings (end-of-year only)
  const suggestedRating = useMemo(() => {
    if (reviewType !== ReviewType.END_YEAR) return null;

    const ratings: ObjectiveRating[] = [];
    const weightages = new Map<string, number>();
    let allRated = true;

    for (const obj of objectives) {
      const rating = objectiveRatings[obj.id];
      if (!rating) { allRated = false; continue; }
      ratings.push({
        objectiveId: obj.id,
        objectiveTitle: obj.title,
        rating: rating as RatingScale,
        finalProgressPercentage: objectiveFinalProgress[obj.id] ?? obj.progressPercentage,
        comments: objectiveComments[obj.id] ?? '',
        managerFinalComment: objectiveComments[obj.id] ?? '',
        evaluationSummary: objectiveEvaluationSummary[obj.id] || undefined,
      });
      weightages.set(obj.id, obj.weightage);
    }

    if (!allRated || ratings.length === 0) return null;
    return calculateWeightedRating(ratings, weightages);
  }, [reviewType, objectives, objectiveRatings, objectiveFinalProgress, objectiveComments, objectiveEvaluationSummary]);

  const validate = (): string[] => {
    const errs: string[] = [];

    if (reviewType === ReviewType.END_YEAR) {
      for (const obj of objectives) {
        if (!objectiveRatings[obj.id]) {
          errs.push(`Rating required for "${obj.title}"`);
        }
        if (!objectiveComments[obj.id]?.trim()) {
          errs.push(`Manager final comment required for "${obj.title}"`);
        }
        if (!objectiveEvaluationSummary[obj.id]) {
          errs.push(`Evaluation summary required for "${obj.title}"`);
        }
      }
      if (!overallRating) errs.push('Overall rating is required');
    }

    if (reviewType === ReviewType.MID_YEAR) {
      if (!overallComments.trim()) errs.push('Overall progress assessment is required');
    }

    if (reviewType === ReviewType.QUARTERLY) {
      if (!overallComments.trim()) errs.push('Quarterly comments are required');
    }

    if (!overallComments.trim() && reviewType === ReviewType.END_YEAR) {
      errs.push('Overall review comments are required');
    }

    return errs;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);

    const ratings: ObjectiveRating[] = objectives.map((obj) => ({
      objectiveId: obj.id,
      objectiveTitle: obj.title,
      rating: (objectiveRatings[obj.id] || RatingScale.MEETS_EXPECTATIONS) as RatingScale,
      finalProgressPercentage: objectiveFinalProgress[obj.id] ?? obj.progressPercentage,
      comments: objectiveComments[obj.id] ?? '',
      managerFinalComment: objectiveComments[obj.id] ?? '',
      evaluationSummary: objectiveEvaluationSummary[obj.id] || undefined,
    }));

    const input: CreateReviewInput = {
      employeeId,
      reviewCycleId,
      reviewType,
      objectiveRatings: reviewType === ReviewType.END_YEAR ? ratings : [],
      overallRating: (overallRating || RatingScale.MEETS_EXPECTATIONS) as RatingScale,
      overallComments,
      developmentSuggestions,
      ...(reviewType === ReviewType.QUARTERLY ? { quarter } : {}),
    };

    onSubmit(input);
  };

  const updateObjectiveRating = (objId: string, value: string) => {
    setObjectiveRatings((prev) => ({ ...prev, [objId]: value ? Number(value) as RatingScale : '' }));
  };

  const updateObjectiveProgress = (objId: string, value: number) => {
    setObjectiveFinalProgress((prev) => ({ ...prev, [objId]: Math.min(100, Math.max(0, value)) }));
  };

  const updateObjectiveComment = (objId: string, value: string) => {
    setObjectiveComments((prev) => ({ ...prev, [objId]: value }));
  };

  const updateEvaluationSummary = (objId: string, value: string) => {
    setObjectiveEvaluationSummary((prev) => ({
      ...prev,
      [objId]: value as ObjectiveEvaluationSummary | '',
    }));
  };

  return (
    <div className="pms-review-form">
      {/* Header */}
      <div className="pms-review-form__header">
        <span className={`pms-review-form__type-badge ${getTypeBadgeClass(reviewType)}`}>
          {getReviewTypeLabel(reviewType)}
        </span>
        <h2 className="pms-review-form__title">
          Review for {employeeName}
        </h2>
        <p className="pms-review-form__subtitle">
          {reviewType === ReviewType.END_YEAR && 'Evaluate each objective and provide an overall rating.'}
          {reviewType === ReviewType.MID_YEAR && 'Provide interim feedback on progress and performance.'}
          {reviewType === ReviewType.QUARTERLY && 'Provide quarterly alignment comments.'}
        </p>
      </div>

      {/* Error summary */}
      {errors.length > 0 && (
        <div className="pms-form-error-summary" role="alert">
          <strong>Please fix the following:</strong>
          <ul>
            {errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>
      )}

      {/* Employee's own self-assessment — read only, shown before the manager's inputs */}
      {employeeSelfReview && (
        <div className="pms-review-form__self-assessment">
          <h3 className="pms-review-form__self-assessment-title">Employee Self-Assessment</h3>
          <div className="pms-review-form__self-assessment-rating">
            <RatingDisplay rating={employeeSelfReview.employeeRating} compact label="Self rating" />
          </div>
          {employeeSelfReview.employeeRatingDescription && (
            <p className="pms-review-form__self-assessment-description">
              {employeeSelfReview.employeeRatingDescription}
            </p>
          )}
          {employeeSelfReview.employeeComment && (
            <p className="pms-review-form__self-assessment-comment">
              {employeeSelfReview.employeeComment}
            </p>
          )}
        </div>
      )}

      {/* Quarter selector for quarterly reviews */}
      {reviewType === ReviewType.QUARTERLY && (
        <div className="pms-review-form__quarter-group pms-form-group">
          <label className="pms-form-label" htmlFor="review-quarter">
            Quarter <span className="required">*</span>
          </label>
          <select
            id="review-quarter"
            className="pms-form-select"
            value={quarter}
            onChange={(e) => setQuarter(Number(e.target.value))}
          >
            {QUARTER_OPTIONS.map((q) => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Objectives list */}
      {objectives.map((obj) => {
        const milestones = milestonesByObjective[obj.id] || [];
        return (
          <div key={obj.id} className="pms-review-form__objective">
            <div className="pms-review-form__obj-header">
              <h3 className="pms-review-form__obj-title">{obj.title}</h3>
              <span className="pms-review-form__obj-weightage">
                {obj.weightage}%
              </span>
            </div>

            <div className="pms-review-form__obj-meta">
              <span>Status: {obj.status.replace('_', ' ')}</span>
              <span>{formatDate(obj.startDate)} – {formatDate(obj.endDate)}</span>
            </div>

            <div className="pms-review-form__obj-progress">
              <ProgressBar
                value={reviewType === ReviewType.END_YEAR
                  ? (objectiveFinalProgress[obj.id] ?? obj.progressPercentage)
                  : obj.progressPercentage}
                label="Progress"
                size="sm"
              />
            </div>

            {/* Milestones */}
            {milestones.length > 0 && (
              <div className="pms-review-form__milestones">
                <h4 className="pms-review-form__milestones-title">Milestones</h4>
                {milestones.map((ms) => (
                  <div key={ms.id} className="pms-review-form__milestone-item">
                    <span className="pms-review-form__milestone-name">{ms.title}</span>
                    <span className="pms-review-form__milestone-progress">
                      {ms.progressPercentage}% · Due {formatDate(ms.dueDate)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* End-of-year: per-objective rating, final progress, comments */}
            {reviewType === ReviewType.END_YEAR && (
              <>
                <div className="pms-review-form__rating-group pms-form-group">
                  <label className="pms-form-label" htmlFor={`rating-${obj.id}`}>
                    Rating <span className="required">*</span>
                  </label>
                  <select
                    id={`rating-${obj.id}`}
                    className={`pms-form-select ${!objectiveRatings[obj.id] && errors.length > 0 ? 'error' : ''}`}
                    value={objectiveRatings[obj.id] ?? ''}
                    onChange={(e) => updateObjectiveRating(obj.id, e.target.value)}
                  >
                    <option value="">Select rating</option>
                    {RATING_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                <div className="pms-form-group">
                  <label className="pms-form-label" htmlFor={`progress-${obj.id}`}>
                    Final Progress Percentage
                  </label>
                  <div className="pms-review-form__progress-control">
                    <ProgressSlider
                      value={objectiveFinalProgress[obj.id] ?? obj.progressPercentage}
                      onChange={(value) => updateObjectiveProgress(obj.id, value)}
                      showMarks
                      ariaLabel={`Final progress percentage for ${obj.title}`}
                    />
                  </div>
                </div>

                <div className="pms-form-group">
                  <label className="pms-form-label" htmlFor={`summary-${obj.id}`}>
                    Evaluation Summary <span className="required">*</span>
                  </label>
                  <select
                    id={`summary-${obj.id}`}
                    className={`pms-form-select ${!objectiveEvaluationSummary[obj.id] && errors.length > 0 ? 'error' : ''}`}
                    value={objectiveEvaluationSummary[obj.id] ?? ''}
                    onChange={(e) => updateEvaluationSummary(obj.id, e.target.value)}
                  >
                    <option value="">Select summary</option>
                    {EVALUATION_SUMMARY_OPTIONS.map((summary) => (
                      <option key={summary} value={summary}>{summary}</option>
                    ))}
                  </select>
                </div>

                <div className="pms-form-group">
                  <label className="pms-form-label" htmlFor={`comments-${obj.id}`}>
                    Manager Final Comment <span className="required">*</span>
                  </label>
                  <textarea
                    id={`comments-${obj.id}`}
                    className={`pms-form-textarea ${!objectiveComments[obj.id]?.trim() && errors.length > 0 ? 'error' : ''}`}
                    value={objectiveComments[obj.id] ?? ''}
                    onChange={(e) => updateObjectiveComment(obj.id, e.target.value)}
                    placeholder="Provide the manager final comment for this objective"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Mid-year: per-objective interim comments */}
            {reviewType === ReviewType.MID_YEAR && (
              <div className="pms-form-group">
                <label className="pms-form-label" htmlFor={`interim-${obj.id}`}>
                  Interim Comments
                </label>
                <textarea
                  id={`interim-${obj.id}`}
                  className="pms-form-textarea"
                  value={objectiveComments[obj.id] ?? ''}
                  onChange={(e) => updateObjectiveComment(obj.id, e.target.value)}
                  placeholder="Provide interim feedback for this objective"
                  rows={2}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Suggested rating (end-of-year only) */}
      {reviewType === ReviewType.END_YEAR && suggestedRating !== null && (
        <div className="pms-review-form__suggested-rating">
          <span className="pms-review-form__suggested-label">Suggested Overall Rating:</span>
          <span className="pms-review-form__suggested-value">
            {suggestedRating.toFixed(2)}
            {' '}({getRatingLabel(Math.round(suggestedRating) as RatingScale)})
          </span>
        </div>
      )}

      {/* Overall section */}
      <div className="pms-review-form__overall">
        <h3 className="pms-review-form__overall-title">Overall Assessment</h3>

        {/* Overall rating (end-of-year only) */}
        {reviewType === ReviewType.END_YEAR && (
          <div className={`pms-review-form__final-rating ${!overallRating && errors.length > 0 ? 'pms-review-form__final-rating--error' : ''}`}>
            <div className="pms-review-form__final-rating-head">
              <span className="pms-review-form__final-rating-icon" aria-hidden="true">★</span>
              <div>
                <h4 className="pms-review-form__final-rating-title">Final Rating</h4>
                <p className="pms-review-form__final-rating-subtitle">Select the final performance outcome for this review.</p>
              </div>
            </div>
            <RatingDisplay
              rating={overallRating}
              interactive
              label="Overall performance"
              onChange={(value) => setOverallRating(value)}
            />
          </div>
        )}

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="overall-comments">
            {reviewType === ReviewType.MID_YEAR ? 'Overall Progress Assessment' :
             reviewType === ReviewType.QUARTERLY ? 'Quarterly Comments' :
             'Overall Review Comments'} <span className="required">*</span>
          </label>
          <textarea
            id="overall-comments"
            className={`pms-form-textarea ${!overallComments.trim() && errors.length > 0 ? 'error' : ''}`}
            value={overallComments}
            onChange={(e) => setOverallComments(e.target.value)}
            placeholder={
              reviewType === ReviewType.MID_YEAR ? 'Provide overall progress assessment' :
              reviewType === ReviewType.QUARTERLY ? 'Provide quarterly alignment comments' :
              'Provide overall performance comments'
            }
            rows={4}
          />
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="dev-suggestions">
            Development Suggestions
          </label>
          <textarea
            id="dev-suggestions"
            className="pms-form-textarea"
            value={developmentSuggestions}
            onChange={(e) => setDevelopmentSuggestions(e.target.value)}
            placeholder="Suggest areas for growth and development"
            rows={3}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="pms-form-actions">
        {onCancel && (
          <button
            type="button"
            className="pms-btn pms-btn-outline"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="pms-btn pms-btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    </div>
  );
};

export default ReviewForm;
